/**
 * 信号质量读数的稳定化展示逻辑。
 *
 * 背景：链路质量是缓变量，但原始值每帧都会有零点几个百分点的抖动。
 * 之前列表直接显示取整后的百分比，于是当真实值正好停在档位边界上时，
 * 读数会在相邻两档之间来回跳，肉眼看到的就是「文字一直在闪」。
 *
 * 这里的做法是：
 *
 * 1. **不展示个位精度**：信号链路不是本平台的监管重点，读数只给
 *    「良好 / 一般 / 稍弱 / 很差」四档（档位与配色共用 `linkQualityTone`），
 *    精确百分比收进 `title`，并在「单机管理」的完整数据列表里可见；
 * 2. **死区**：只有原始值深入下一档 {@link LINK_QUALITY_DEAD_BAND} 个百分点才允许换档，
 *    刚好压在边界上的抖动会被挡掉；
 * 3. **驻留**：换档还要连续 {@link LINK_QUALITY_DWELL_FRAMES} 帧得到同一结果，
 *    跨两档以上的真实劣化则立即生效，不会因为等待而漏报。
 *
 * 状态机是纯函数，且按遥测帧号（`DroneTelemetry.seq`）推进：
 * React 重复渲染同一帧不会多算一帧，未变化时直接返回入参本身，
 * 组件可以放心地在渲染期用 props 派生 state。
 */
import { linkQualityTone } from './droneStatus';

export type LinkQualityTone = ReturnType<typeof linkQualityTone>;

/** 档位名称：只描述「链路的可用程度」，不假装精确到个位。 */
export const LINK_QUALITY_LABELS: Readonly<Record<LinkQualityTone, string>> = {
  ok: '良好',
  caution: '一般',
  warning: '稍弱',
  critical: '很差',
};

/** 换档死区（百分点）：贴着边界抖动的原始值不会引起换档。 */
export const LINK_QUALITY_DEAD_BAND = 3;

/** 换档需要连续成立的帧数（1Hz 遥测下即 2 秒）。 */
export const LINK_QUALITY_DWELL_FRAMES = 2;

/** 严重度排序，用于判断「跨两档」的真实劣化。 */
const RANK: Readonly<Record<LinkQualityTone, number>> = {
  critical: 0,
  warning: 1,
  caution: 2,
  ok: 3,
};

export interface LinkQualityDisplay {
  /** 已经生效、正在显示的档位 */
  readonly level: LinkQualityTone;
  /** 正在观察中的候选档位 */
  readonly candidate: LinkQualityTone | null;
  /** 候选档位已连续出现的帧数 */
  readonly hits: number;
  /** 已计入的最后一个遥测帧号 */
  readonly frame: number;
}

/**
 * 带死区的目标档位：只有原始值「深入」下一档才返回新档位。
 *
 * 双向都用 `linkQualityTone` 判定（升档查 `value - 死区`，降档查 `value + 死区`）：
 * 把原始值朝当前档位方向拉回一个死区后仍落在当前档位里，就说明它只是贴着边界抖动，
 * 读数保持不动；否则才算真的换档。这样这里不需要再重复一份档位阈值。
 */
export function stickyLinkQualityLevel(value: number, current: LinkQualityTone): LinkQualityTone {
  const tentative = linkQualityTone(value);
  if (tentative === current) return current;
  const rising = RANK[tentative] > RANK[current];
  const inside = linkQualityTone(value + (rising ? -LINK_QUALITY_DEAD_BAND : LINK_QUALITY_DEAD_BAND));
  return inside === current ? current : tentative;
}

export function initialLinkQualityDisplay(value: number, frame: number): LinkQualityDisplay {
  return { level: linkQualityTone(value), candidate: null, hits: 0, frame };
}

/**
 * 推进一帧，返回新的显示状态。
 *
 * 同一帧号重复调用是幂等的；不需要变化时返回入参本身，
 * 组件里可以直接用引用比较判断「状态是否真的变了」。
 */
export function nextLinkQualityDisplay(
  state: LinkQualityDisplay,
  value: number,
  frame: number,
): LinkQualityDisplay {
  if (state.frame === frame) return state;

  const target = stickyLinkQualityLevel(value, state.level);
  const settle = (level: LinkQualityTone): LinkQualityDisplay => ({ level, candidate: null, hits: 0, frame });

  if (target === state.level) {
    return state.candidate === null ? state : settle(state.level);
  }
  if (Math.abs(RANK[target] - RANK[state.level]) >= 2) return settle(target);
  if (state.candidate === target) {
    const hits = state.hits + 1;
    return hits >= LINK_QUALITY_DWELL_FRAMES ? settle(target) : { ...state, hits, frame };
  }
  return { level: state.level, candidate: target, hits: 1, frame };
}
